import { useRef, useState } from 'react';
import { Plus } from 'lucide-react';
import { DueDatePicker } from './DueDatePicker';

export interface QuickAddTaskProps {
  onAdd: (task: { title: string; dueDate: string | null; dueTime: string | null }) => void;
  todayKey: string;
  eventStartAt?: Date | string | null;
  /** Templates carry offsets, not dates — the date picker is left out. */
  withDate?: boolean;
  /** The list's due dates carry a time — a time field follows the date once one is chosen. */
  withTime?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
}

/**
 * Type a task and press Enter. The field stays focused and empties, so a run
 * of tasks goes in without touching the mouse; a due date is one tap away and
 * applies to that one task only.
 */
export function QuickAddTask({
  onAdd,
  todayKey,
  eventStartAt,
  withDate = true,
  withTime = false,
  placeholder = 'Add a task…',
  autoFocus = false,
}: QuickAddTaskProps) {
  const [title, setTitle] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [dueTime, setDueTime] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  function submit() {
    const trimmed = title.trim();
    if (!trimmed) return;
    onAdd({
      title: trimmed,
      dueDate: dueDate || null,
      dueTime: withTime && dueDate && dueTime ? dueTime : null,
    });
    setTitle('');
    setDueDate('');
    setDueTime('');
    inputRef.current?.focus();
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
      className="flex items-center gap-2 px-3 py-1.5"
    >
      <Plus size={18} className="shrink-0 text-gray-400" aria-hidden />
      <input
        ref={inputRef}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setTitle('');
        }}
        placeholder={placeholder}
        aria-label={placeholder}
        autoFocus={autoFocus}
        maxLength={500}
        className="flex-1 min-w-0 py-1.5 text-sm bg-transparent border-0 border-b border-transparent focus:border-primary-400 focus:outline-none focus:ring-0 placeholder:text-gray-400"
      />
      {withDate && (
        <DueDatePicker
          value={dueDate}
          onChange={setDueDate}
          todayKey={todayKey}
          eventStartAt={eventStartAt}
          placeholder="No date"
          className="py-1 text-xs"
        />
      )}
      {withDate && withTime && dueDate && (
        <input
          type="time"
          value={dueTime}
          onChange={(e) => setDueTime(e.target.value)}
          aria-label="Due time"
          className="w-28 px-2 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
        />
      )}
      <button
        type="submit"
        disabled={!title.trim()}
        className="px-3 py-1.5 text-xs font-medium rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-40"
      >
        Add
      </button>
    </form>
  );
}
